import React, { memo, useState } from "react";

import CommonCard from "src/components/Card/Card";
import { METRIC, getMetricColor } from "src/components/MultiDateTimeCalendar/calendarUtils";
import InfoBanner from "../InfoBanner/InfoBanner";
import RateTable from "../RateTable/RateTable";

import SectionHeader from "./SectionHeader";
import { ChevronDownIcon, InfoIcon } from "./icons";

const ESTIMATE_NOTE = "Payment amount is estimated and may change after all pay requests are processed.";

// Contracts whose tier breakdown is worth surfacing in a tooltip next to the
// estimated total — mirrors the mobile TransactionalDetailsSection's check.
const TIER_TOOLTIP_RATE_TYPES = [
  "step",
  "stemi",
  "gen-cardio",
  "multisite-multiregion-step",
  "multisite-multiregion-flat",
  "multisite",
];

/*
 * Estimated total plus the per-date rate breakdown. Collapsible, like the
 * mobile section.
 */
const TransactionalDetailsSection = ({
  rows,
  estimatedAmount,
  estimatedDuration,
  onTimeChange,
  onDeselect,
  showEstimateNote = false,
  disabled = false,
  isShiftContract = false,
  multiTierStr = "",
  rateType = null,
  thresholdUnitType = null,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showTierTooltip, setShowTierTooltip] = useState(false);

  const showTierInfo = Boolean(multiTierStr) && TIER_TOOLTIP_RATE_TYPES.includes(rateType);
  const tierLines = showTierInfo ? multiTierStr.split("\n") : [];
  // Same metric-to-color mapping the calendar cells use, so the total mirrors
  // the calendar's shift (teal) vs hours (blue) colouring for this contract.
  const metricColor = getMetricColor(isShiftContract ? { value: METRIC.QUANTITY } : { value: METRIC.HOURS });

  return (
    <section className="add-pay-request__section">
      <div>
        <CommonCard className="add-pay-request__card" style>
          <div style={{ padding: "8px 0px 8px 8px" }}>
            <SectionHeader title="Transactional Details" />
          </div>
          <div style={{ padding: "8px" }}>
            <div className="add-pay-request__total-row">
            <div className="add-pay-request__total-text">
              <span className="add-pay-request__total-label">Estimated Total</span>
              <span className="add-pay-request__total-values">
                <span className="add-pay-request__total-value" style={{ color: metricColor }}>
                  {estimatedAmount}
                </span>
                <span className="add-pay-request__total-dot">•</span>
                <span className="add-pay-request__total-value" style={{ color: metricColor }}>
                  {estimatedDuration}
                </span>
              </span>
            </div>

            <div className="add-pay-request__total-actions">
              {showTierInfo && (
                <span
                  className="add-pay-request__tier-info"
                  onMouseEnter={() => setShowTierTooltip(true)}
                  onMouseLeave={() => setShowTierTooltip(false)}
                >
                  <InfoIcon />
                  {showTierTooltip && (
                    <div className="add-pay-request__tier-tooltip">
                      {tierLines.map((line) => (
                        <div key={line} className="add-pay-request__tier-tooltip-row">
                          {line}
                        </div>
                      ))}
                    </div>
                  )}
                </span>
              )}

              <button
                type="button"
                className={`add-pay-request__collapse ${expanded ? "add-pay-request__collapse--open" : ""}`}
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-label="Toggle transactional details"
              >
                <ChevronDownIcon />
              </button>
            </div>
          </div>

            {expanded && (
              <RateTable
                rows={rows}
                onTimeChange={onTimeChange}
                onRemove={onDeselect}
                disabled={disabled}
                isShiftContract={isShiftContract}
              />
            )}
            </div>
        </CommonCard>
        <div className="custom-banner-container">
          {showEstimateNote && (
            <InfoBanner variant="muted" attach="bottom" text={ESTIMATE_NOTE} />
          )}
        </div>
      </div>
    </section>
  );
};

export default memo(TransactionalDetailsSection);
